/**
 * @fileoverview Modal component for creating, editing, and managing Memorama (Memory 1 vs 1) visual themes.
 * Supports a 4-tab configuration:
 * 1. Identidad & Portada (Cover branding, logos, background, title, contrast colors)
 * 2. Escenario Estadio (Screen stadium scene background, text/score colors, typography)
 * 3. Mazo de Cartas & Dorso (Symmetric/Asymmetric decks, batch Drag & Drop SkiaSharp upload, card back texture/logo/color)
 * 4. Jugadores & Celular (Default names, avatars, victory celebration poses, player colors, mobile fan backgrounds)
 * 
 * @module components/Admin/Games/Memory/MemoryThemeModal
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import {
    UploadAsset,
    UploadBatchCards,
    SaveTheme
} from '../../../../utilities/BL/Memory';

/**
 * Resolves relative media paths into absolute URLs for browser preview.
 * 
 * @param {string} mediaPath - Relative or absolute media URL.
 * @returns {string} Fully qualified preview URL.
 */
export const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/').replace('/api/', '/').replace('/api', '/');
    return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Reusable Drag & Drop Field component for individual media uploads.
 * 
 * @function DragAndDropField
 * @param {Object} props - Component properties.
 * @param {string} props.fieldLabel - Label displayed above the drop area.
 * @param {string} props.targetFieldName - Form property key being updated.
 * @param {string} props.currentValue - Current asset URL.
 * @param {Function} props.onUploadFile - Handler triggered when a file is dropped or selected.
 * @param {Function} props.resolveMediaUrl - URL resolution helper for preview.
 * @param {boolean} [props.isProcessing=false] - True when upload is active.
 * @param {string} [props.helpText=''] - Optional description below the dropzone.
 * @param {Function} [props.onClear=''] - Optional clear button callback.
 */
const DragAndDropField = ({
    fieldLabel,
    targetFieldName,
    currentValue,
    onUploadFile,
    resolveMediaUrl,
    isProcessing = false,
    helpText = '',
    onClear = null
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = () => {
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsDragHoverActive(false);
        const selectedFile = dropEvent.dataTransfer.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, targetFieldName);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFile = changeEvent.target.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, targetFieldName);
        }
    };

    return (
        <div className="mb-3">
            {fieldLabel && (
                <label className="form-label fw-bold text-dark mb-1 d-flex justify-content-between align-items-center">
                    <span>{fieldLabel}:</span>
                    {currentValue && onClear && (
                        <button
                            type="button"
                            className="btn btn-link text-danger p-0 text-decoration-none small"
                            onClick={onClear}
                            style={{ fontSize: '0.75rem' }}
                        >
                            <i className="bi bi-trash me-1"></i>Quitar
                        </button>
                    )}
                </label>
            )}
            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center transition ${
                    isDragHoverActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetFieldName}`).click()}
                style={{ minHeight: '90px', cursor: 'pointer' }}
            >
                {isProcessing ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y optimizando (WebP)...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-4 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </>
                )}
                <input
                    id={`file-input-${targetFieldName}`}
                    type="file"
                    className="d-none"
                    accept="image/*"
                    onChange={handleNativeFileChange}
                />
            </div>
            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative">
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt="Vista previa"
                        style={{ maxHeight: '75px', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(errorEvent) => {
                            errorEvent.target.style.display = 'none';
                        }}
                    />
                    <div className="small text-truncate text-muted mt-1" style={{ maxWidth: '100%' }}>
                        {currentValue}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Dropzone for uploading multiple card deck images simultaneously with Drag & Drop.
 * 
 * @function BatchDeckDropzone
 * @param {Object} props - Component properties.
 * @param {Function} props.onUploadBatch - Handler for uploading an array of files.
 * @param {boolean} props.isUploading - Indicator if batch upload is active.
 * @param {string} [props.title=''] - Custom title for the dropzone.
 * @param {string} [props.inputId='batch-deck-file-input'] - Unique input ID.
 */
const BatchDeckDropzone = ({
    onUploadBatch,
    isUploading,
    title = 'Carga Masiva de Baraja de Cartas (PNG/JPG)',
    inputId = 'batch-deck-file-input'
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsHovered(true);
    };

    const handleDragLeave = () => {
        setIsHovered(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsHovered(false);
        const droppedFiles = Array.from(dropEvent.dataTransfer.files).filter((fileItem) =>
            fileItem.type.startsWith('image/')
        );
        if (droppedFiles.length > 0) {
            onUploadBatch(droppedFiles);
        }
    };

    const handleInputChange = (inputEvent) => {
        const selectedFiles = Array.from(inputEvent.target.files).filter((fileItem) =>
            fileItem.type.startsWith('image/')
        );
        if (selectedFiles.length > 0) {
            onUploadBatch(selectedFiles);
        }
    };

    return (
        <div
            className={`border border-2 border-dashed rounded-4 p-4 text-center mb-4 transition ${
                isHovered ? 'border-success bg-success-subtle shadow' : 'border-primary bg-light'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById(inputId).click()}
            style={{ cursor: 'pointer' }}
        >
            <i className="bi bi-grid-3x3-gap-fill text-primary fs-1 d-block mb-2"></i>
            <h5 className="fw-bold text-dark mb-1">{title}</h5>
            <p className="text-muted small mb-2">
                Selecciona o arrastra un lote de imágenes (mínimo 4, recomendado 8 a 16) para poblar automáticamente los pares del mazo.
            </p>
            {isUploading ? (
                <div className="alert alert-info py-2 d-inline-block mb-0">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <strong>Optimizando lote con SkiaSharp a WebP...</strong>
                </div>
            ) : (
                <span className="btn btn-sm btn-outline-primary fw-bold px-3">
                    <i className="bi bi-plus-circle me-1"></i> Explorar Archivos Múltiples
                </span>
            )}
            <input
                id={inputId}
                type="file"
                multiple
                accept="image/*"
                className="d-none"
                onChange={handleInputChange}
            />
        </div>
    );
};

/**
 * Memory Theme Modal Component.
 * 
 * @function MemoryThemeModal
 * @param {Object} props - Properties.
 * @param {boolean} [props.isOpen=false] - Modal open flag.
 * @param {boolean} [props.show] - Alternative open flag.
 * @param {Function} props.onCloseModal - Callback to dismiss modal.
 * @param {Function} [props.onClose] - Alternative close callback.
 * @param {Object|null} [props.initialThemeData=null] - Theme object when editing, or null for creating.
 * @param {Object|null} [props.themeToEdit=null] - Alternative theme edit prop.
 * @param {Function} [props.onThemeSavedSuccessfully] - Callback on save success.
 * @param {Function} [props.onSave] - Alternative save callback.
 * @param {string|null} [props.roomId=null] - Room ID GUID.
 * @param {string|null} [props.operatorId=null] - Operator ID GUID.
 * @returns {React.ReactElement|null} Modal element.
 */
export const MemoryThemeModal = ({
    isOpen: propIsOpen,
    show,
    onCloseModal: propOnCloseModal,
    onClose,
    initialThemeData: propInitialThemeData,
    themeToEdit,
    onThemeSavedSuccessfully: propOnThemeSavedSuccessfully,
    onSave,
    roomId = null,
    operatorId = null
}) => {
    const isOpen = propIsOpen !== undefined ? propIsOpen : show;
    const onCloseModal = propOnCloseModal || onClose;
    const initialThemeData = propInitialThemeData || themeToEdit;
    const onThemeSavedSuccessfully = propOnThemeSavedSuccessfully || onSave;

    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('identity'); // 'identity' | 'screen' | 'deck' | 'players'
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');
    const [isBatchUploading, setIsBatchUploading] = useState(false);
    const [activeAsymmetricDeckTab, setActiveAsymmetricDeckTab] = useState('p1'); // 'p1' | 'p2'

    // Default Theme Structure
    const defaultThemeStructure = {
        id: 0,
        name: '',
        isSystemPreset: false,
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        coverTitle: 'MEMORAMA 1 VS 1',

        // 1. Portada
        coverBackgroundType: 'color',
        coverBackgroundColor: '#1e1b4b',
        coverBackgroundUrl: '',
        coverTextColor: '#ffffff',

        // 2. Escenario Estadio (/screen)
        gameBackgroundType: 'color',
        gameBackgroundColor: '#0f172a',
        gameBackgroundUrl: '',
        gameTextColor: '#ffffff',
        fontFamily: 'Fredoka',

        // 3. Mazo de Cartas & Dorso
        useSameDeckForAll: true,
        sharedDeckImages: [],
        deckImagesP1: [],
        deckImagesP2: [],
        cardBackType: 'color',
        cardBackBackgroundColor: '#1e293b',
        cardBackLogoUrl: '',

        // 4. Jugadores & Celular
        player1Name: 'Jugador 1',
        player1Avatar: '',
        player1Winner: '',
        player1Color: '#fbbf24',

        player2Name: 'Jugador 2',
        player2Avatar: '',
        player2Winner: '',
        player2Color: '#60a5fa',

        fanBackgroundType: 'color',
        fanBackgroundColor: '#0f172a',
        fanBackgroundUrl: '',
        fanTextColor: '#ffffff'
    };

    const [themeFormState, setThemeFormState] = useState(defaultThemeStructure);

    // Parse array/JSON strings helper
    const parseDeckArray = (rawDeck) => {
        if (!rawDeck) return [];
        if (Array.isArray(rawDeck)) return rawDeck;
        if (typeof rawDeck === 'string') {
            try {
                const parsed = JSON.parse(rawDeck);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) {
                return rawDeck.split(',').map((s) => s.trim()).filter(Boolean);
            }
        }
        return [];
    };

    useEffect(() => {
        if (initialThemeData && (initialThemeData.id > 0 || initialThemeData.name)) {
            setThemeFormState({
                ...defaultThemeStructure,
                ...initialThemeData,
                coverTitle: initialThemeData.coverTitle || 'MEMORAMA 1 VS 1',
                useSameDeckForAll: initialThemeData.useSameDeckForAll !== undefined
                    ? Boolean(initialThemeData.useSameDeckForAll)
                    : (initialThemeData.UseSameDeckForAll !== undefined ? Boolean(initialThemeData.UseSameDeckForAll) : true),
                sharedDeckImages: parseDeckArray(initialThemeData.sharedDeckImages || initialThemeData.SharedDeckImages),
                deckImagesP1: parseDeckArray(initialThemeData.deckImagesP1 || initialThemeData.DeckImagesP1),
                deckImagesP2: parseDeckArray(initialThemeData.deckImagesP2 || initialThemeData.DeckImagesP2)
            });
        } else {
            setThemeFormState(defaultThemeStructure);
        }
        setActiveTabKey('identity');
    }, [initialThemeData, isOpen]);

    if (!isOpen) return null;

    /**
     * Updates an individual scalar property on the theme form state.
     */
    const handleFieldChange = (fieldName, fieldValue) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    /**
     * Single media asset upload handler.
     */
    const handleSingleFileUpload = async (fileObject, targetFieldName) => {
        setActiveUploadFieldKey(targetFieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y optimizando archivo...' });

        try {
            const uploadResponse = await UploadAsset(fileObject, roomId);
            const uploadedUrl = uploadResponse.url || uploadResponse.data?.url || (uploadResponse.data && typeof uploadResponse.data === 'string' ? uploadResponse.data : '');
            if (uploadResponse.success && uploadedUrl) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo cargado con éxito.' });
                handleFieldChange(targetFieldName, uploadedUrl);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'Error al subir el archivo multimedia.'
                });
            }
        } catch (uploadException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al subir el archivo.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    /**
     * Batch cards upload handler for shared or individual player decks.
     */
    const handleBatchDeckUpload = async (filesArray, targetDeckKey = 'sharedDeckImages') => {
        if (filesArray.length === 0) return;
        setIsBatchUploading(true);
        showMessage({
            type: MESSAGE_TYPE.INFO,
            text: `Subiendo ${filesArray.length} imágenes para el mazo de cartas...`
        });

        try {
            const batchResponse = await UploadBatchCards(filesArray, roomId);
            const extractedUrls = batchResponse.urls ||
                batchResponse.data?.urls ||
                (Array.isArray(batchResponse.data?.files) ? batchResponse.data.files.filter((f) => f.success && f.url).map((f) => f.url) : []) ||
                (Array.isArray(batchResponse.files) ? batchResponse.files.filter((f) => f.success && f.url).map((f) => f.url) : []) ||
                [];

            if (batchResponse.success && extractedUrls.length > 0) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Se agregaron ${extractedUrls.length} imágenes a la baraja.`
                });
                setThemeFormState((previousState) => {
                    const currentDeck = Array.isArray(previousState[targetDeckKey]) ? previousState[targetDeckKey] : [];
                    const combinedDeck = [...currentDeck, ...extractedUrls].slice(0, 30);
                    return {
                        ...previousState,
                        [targetDeckKey]: combinedDeck
                    };
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: batchResponse.message || 'Error al procesar el lote de imágenes.'
                });
            }
        } catch (batchException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado durante la subida en lote.' });
        } finally {
            setIsBatchUploading(false);
        }
    };

    /**
     * Replaces an individual slot in a deck.
     */
    const handleSingleDeckSlotUpload = async (fileObject, slotIndex, targetDeckKey = 'sharedDeckImages') => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Actualizando carta #${slotIndex + 1}...` });
        try {
            const uploadResponse = await UploadAsset(fileObject, roomId);
            const uploadedUrl = uploadResponse.url || uploadResponse.data?.url || '';
            if (uploadResponse.success && uploadedUrl) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Carta #${slotIndex + 1} actualizada.` });
                setThemeFormState((previousState) => {
                    const updatedDeck = [...(previousState[targetDeckKey] || [])];
                    updatedDeck[slotIndex] = uploadedUrl;
                    return {
                        ...previousState,
                        [targetDeckKey]: updatedDeck
                    };
                });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: uploadResponse.message || 'Error al actualizar carta.' });
            }
        } catch (slotException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al actualizar la carta.' });
        }
    };

    /**
     * Removes an image from a deck.
     */
    const handleRemoveDeckSlot = (slotIndexToRemove, targetDeckKey = 'sharedDeckImages') => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [targetDeckKey]: (previousState[targetDeckKey] || []).filter((_, index) => index !== slotIndexToRemove)
        }));
    };

    /**
     * Clears all cards in a deck.
     */
    const handleClearDeck = (targetDeckKey = 'sharedDeckImages') => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [targetDeckKey]: []
        }));
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Baraja vaciada.' });
    };

    /**
     * Submits the theme form to be saved on the backend.
     */
    const handleFormSubmit = async (formEvent) => {
        formEvent.preventDefault();

        if (!themeFormState.name || themeFormState.name.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Por favor asigna un nombre al tema.' });
            setActiveTabKey('identity');
            return;
        }

        // Validate that deck has at least 4 cards for a minimal 4-pair game
        const activeDeckCount = themeFormState.useSameDeckForAll
            ? (themeFormState.sharedDeckImages?.length || 0)
            : Math.min(themeFormState.deckImagesP1?.length || 0, themeFormState.deckImagesP2?.length || 0);

        if (activeDeckCount < 4) {
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: 'La baraja debe contener al menos 4 imágenes de cartas para formar los pares de la partida.'
            });
            setActiveTabKey('deck');
            return;
        }

        setIsSavingInProgress(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración del tema...' });

        try {
            const payloadToSave = {
                ...themeFormState,
                name: themeFormState.name.trim(),
                roomId: themeFormState.roomId || roomId,
                operatorId: themeFormState.operatorId || operatorId,
                sharedDeckImages: themeFormState.sharedDeckImages || [],
                deckImagesP1: themeFormState.deckImagesP1 || [],
                deckImagesP2: themeFormState.deckImagesP2 || []
            };

            const saveResponse = await SaveTheme(payloadToSave, roomId);
            if (saveResponse.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Tema "${themeFormState.name}" guardado exitosamente.`
                });
                if (onThemeSavedSuccessfully) {
                    onThemeSavedSuccessfully(saveResponse.data);
                }
                if (onCloseModal) {
                    onCloseModal();
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResponse.message || 'Error al guardar el tema.'
                });
            }
        } catch (saveException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el tema.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    // Helper to render deck grid
    const renderDeckGrid = (deckArray, deckKey) => (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h6 className="fw-bold text-primary m-0">
                        <i className="bi bi-grid-3x3-gap-fill me-1"></i> Cartas en Mazo ({deckArray.length} pares posibles)
                    </h6>
                    <small className="text-muted">
                        Cada imagen representará un par a encontrar. Se recomienda un mínimo de 4 a 12 imágenes.
                    </small>
                </div>
                {deckArray.length > 0 && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger fw-bold"
                        onClick={() => handleClearDeck(deckKey)}
                    >
                        <i className="bi bi-trash me-1"></i> Vaciar Baraja
                    </button>
                )}
            </div>

            {/* Dropzone Masivo */}
            <BatchDeckDropzone
                onUploadBatch={(files) => handleBatchDeckUpload(files, deckKey)}
                isUploading={isBatchUploading}
                title={`Cargar Lote de Imágenes para ${deckKey === 'deckImagesP1' ? 'Mazo Jugador 1' : deckKey === 'deckImagesP2' ? 'Mazo Jugador 2' : 'Mazo Compartido'}`}
                inputId={`batch-deck-${deckKey}`}
            />

            {/* Grilla visual de cartas */}
            <div className="row g-3">
                {deckArray.map((cardUrl, cardIdx) => (
                    <div key={cardIdx} className="col-6 col-sm-4 col-md-3 col-lg-2">
                        <div className="card h-100 border shadow-sm rounded-3 overflow-hidden text-center position-relative">
                            <span className="badge bg-dark position-absolute top-0 start-0 m-1">
                                #{cardIdx + 1}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0 px-1 rounded-circle"
                                onClick={() => handleRemoveDeckSlot(cardIdx, deckKey)}
                                title="Eliminar esta carta"
                            >
                                <i className="bi bi-x"></i>
                            </button>
                            <div
                                className="p-2 bg-light d-flex align-items-center justify-content-center"
                                style={{ height: '110px' }}
                            >
                                <img
                                    src={resolveMediaUrl(cardUrl)}
                                    alt={`Carta ${cardIdx + 1}`}
                                    style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }}
                                    onError={(e) => {
                                        e.target.src = '/img/games/slotmachine/logotipo-five-digital-minds-azul.png';
                                    }}
                                />
                            </div>
                            <div className="p-2 border-top bg-white">
                                <label
                                    htmlFor={`card-slot-input-${deckKey}-${cardIdx}`}
                                    className="btn btn-xs btn-outline-primary w-100 py-1 fw-bold text-truncate"
                                    style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                                >
                                    <i className="bi bi-arrow-repeat me-1"></i> Reemplazar
                                </label>
                                <input
                                    id={`card-slot-input-${deckKey}-${cardIdx}`}
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) handleSingleDeckSlotUpload(file, cardIdx, deckKey);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <>
            <div className="cModal__Background" onClick={onCloseModal}></div>
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '1020px', width: '95vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-grid-3x3-gap-fill text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {themeFormState.id > 0 ? `Editar Tema: ${themeFormState.name}` : 'Crear Nuevo Tema - Memorama 1 vs 1'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={onCloseModal}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Navigation Tabs Header */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'identity' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('identity')}
                                >
                                    <i className="bi bi-palette me-1"></i> 1. Identidad & Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'screen' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('screen')}
                                >
                                    <i className="bi bi-tv me-1"></i> 2. Escenario Estadio (/screen)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'deck' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('deck')}
                                >
                                    <i className="bi bi-grid-3x3 me-1"></i> 3. Mazo de Cartas & Dorso (
                                    {themeFormState.useSameDeckForAll
                                        ? `${themeFormState.sharedDeckImages?.length || 0} cartas`
                                        : `J1: ${themeFormState.deckImagesP1?.length || 0} / J2: ${themeFormState.deckImagesP2?.length || 0}`}
                                    )
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'players' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('players')}
                                >
                                    <i className="bi bi-people me-1"></i> 4. Jugadores & Celular
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Scrollable Tab Body */}
                    <form onSubmit={handleFormSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 170px)' }}>
                            {/* ============================================================ */}
                            {/* PESTAÑA 1: IDENTIDAD & PORTADA                               */}
                            {/* ============================================================ */}
                            {activeTabKey === 'identity' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Nombre del Tema *</label>
                                        <input
                                            type="text"
                                            className="form-control fw-bold"
                                            placeholder="ej. Memorama Clásico Club, Especial Patrocinador..."
                                            value={themeFormState.name}
                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DragAndDropField
                                            fieldLabel="Logo de Marca / Club (Brand)"
                                            targetFieldName="brandLogoUrl"
                                            currentValue={themeFormState.brandLogoUrl}
                                            onUploadFile={handleSingleFileUpload}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isProcessing={activeUploadFieldKey === 'brandLogoUrl'}
                                            helpText="Logo institucional o del equipo (PNG transparente)"
                                            onClear={() => handleFieldChange('brandLogoUrl', '')}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DragAndDropField
                                            fieldLabel="Logo del Patrocinador Oficial (Sponsor)"
                                            targetFieldName="sponsorLogoUrl"
                                            currentValue={themeFormState.sponsorLogoUrl}
                                            onUploadFile={handleSingleFileUpload}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isProcessing={activeUploadFieldKey === 'sponsorLogoUrl'}
                                            helpText="Logo comercial o sponsor (PNG transparente)"
                                            onClear={() => handleFieldChange('sponsorLogoUrl', '')}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <hr className="my-2" />
                                        <h6 className="fw-bold text-primary mb-3">
                                            <i className="bi bi-aspect-ratio me-1"></i> Personalización de la Portada Promocional (/screen)
                                        </h6>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Título de Portada</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="MEMORAMA 1 VS 1"
                                            value={themeFormState.coverTitle || ''}
                                            onChange={(e) => handleFieldChange('coverTitle', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Texto de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipo de Fondo de Portada</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.coverBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('coverBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen / Textura</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Fondo de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverBackgroundColor || '#1e1b4b'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.coverBackgroundColor || '#1e1b4b'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.coverBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo para Portada (1920x1080)"
                                                targetFieldName="coverBackgroundUrl"
                                                currentValue={themeFormState.coverBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'coverBackgroundUrl'}
                                                onClear={() => handleFieldChange('coverBackgroundUrl', '')}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 2: ESCENARIO ESTADIO (/screen)                       */}
                            {/* ============================================================ */}
                            {activeTabKey === 'screen' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <h6 className="fw-bold text-primary mb-2">
                                            <i className="bi bi-tv me-1"></i> Fondo y Tipografía del Escenario de Juego (/screen)
                                        </h6>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo de Juego</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.gameBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('gameBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen de Estadio / Tablero</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo de Juego</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.gameBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto / Marcadores</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.gameBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo de Juego (1920x1080)"
                                                targetFieldName="gameBackgroundUrl"
                                                currentValue={themeFormState.gameBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'gameBackgroundUrl'}
                                                onClear={() => handleFieldChange('gameBackgroundUrl', '')}
                                            />
                                        </div>
                                    )}

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipografía del Escenario</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.fontFamily || 'Fredoka'}
                                            onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                                        >
                                            <option value="Fredoka">Fredoka (Divertida / Redonda)</option>
                                            <option value="Bangers">Bangers (Cómic / Impacto)</option>
                                            <option value="Montserrat">Montserrat (Moderna / Deportiva)</option>
                                            <option value="Poppins">Poppins (Geométrica)</option>
                                            <option value="Inter">Inter (Limpia / Minimalista)</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 3: MAZO DE CARTAS & DORSO                            */}
                            {/* ============================================================ */}
                            {activeTabKey === 'deck' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        {/* Switch Simétrico / Asimétrico */}
                                        <div className="p-3 bg-light rounded-3 border mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div>
                                                <h6 className="fw-bold text-dark m-0">
                                                    <i className="bi bi-shuffle me-2 text-primary"></i>
                                                    Modo de Baraja: {themeFormState.useSameDeckForAll ? 'Mazo Simétrico (Compartido)' : 'Mazo Asimétrico (Independiente P1 vs P2)'}
                                                </h6>
                                                <small className="text-muted">
                                                    {themeFormState.useSameDeckForAll
                                                        ? 'Ambos jugadores compiten buscando los mismos pares en tableros barajados independientemente.'
                                                        : 'Cada jugador posee un catálogo de cartas y marcas propio asignado a su tablero.'}
                                                </small>
                                            </div>
                                            <div className="form-check form-switch fs-5 m-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="toggleUseSameDeckForAll"
                                                    checked={themeFormState.useSameDeckForAll}
                                                    onChange={(e) => handleFieldChange('useSameDeckForAll', e.target.checked)}
                                                />
                                                <label className="form-check-label small fw-bold ms-2" htmlFor="toggleUseSameDeckForAll">
                                                    {themeFormState.useSameDeckForAll ? 'Mismo Mazo para Todos' : 'Mazo Asimétrico'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Subsección de Dorso de Carta */}
                                    <div className="col-12">
                                        <div className="p-3 bg-light rounded-3 border mb-3">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-card-heading me-1"></i> Personalización del Reverso / Dorso de las Cartas
                                            </h6>
                                            <div className="row g-3">
                                                <div className="col-md-4">
                                                    <label className="form-label fw-bold">Tipo de Dorso</label>
                                                    <select
                                                        className="form-select"
                                                        value={themeFormState.cardBackType || 'color'}
                                                        onChange={(e) => handleFieldChange('cardBackType', e.target.value)}
                                                    >
                                                        <option value="color">Color con Logo Central</option>
                                                        <option value="image">Imagen / Textura Completa</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label fw-bold">Color de Fondo del Dorso</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color"
                                                            value={themeFormState.cardBackBackgroundColor || '#1e293b'}
                                                            onChange={(e) => handleFieldChange('cardBackBackgroundColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control text-uppercase"
                                                            value={themeFormState.cardBackBackgroundColor || '#1e293b'}
                                                            onChange={(e) => handleFieldChange('cardBackBackgroundColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <DragAndDropField
                                                        fieldLabel="Logo / Imagen del Dorso"
                                                        targetFieldName="cardBackLogoUrl"
                                                        currentValue={themeFormState.cardBackLogoUrl}
                                                        onUploadFile={handleSingleFileUpload}
                                                        resolveMediaUrl={resolveMediaUrl}
                                                        isProcessing={activeUploadFieldKey === 'cardBackLogoUrl'}
                                                        helpText="Logo del club o patrocinador (PNG)"
                                                        onClear={() => handleFieldChange('cardBackLogoUrl', '')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Grilla de Cartas: Compartida vs Asimétrica */}
                                    <div className="col-12">
                                        {themeFormState.useSameDeckForAll ? (
                                            renderDeckGrid(themeFormState.sharedDeckImages || [], 'sharedDeckImages')
                                        ) : (
                                            <div>
                                                <ul className="nav nav-pills mb-3">
                                                    <li className="nav-item">
                                                        <button
                                                            type="button"
                                                            className={`nav-link fw-bold ${activeAsymmetricDeckTab === 'p1' ? 'active bg-warning text-dark' : 'text-secondary'}`}
                                                            onClick={() => setActiveAsymmetricDeckTab('p1')}
                                                        >
                                                            <i className="bi bi-person-fill me-1"></i> Mazo Jugador 1 ({themeFormState.deckImagesP1?.length || 0})
                                                        </button>
                                                    </li>
                                                    <li className="nav-item ms-2">
                                                        <button
                                                            type="button"
                                                            className={`nav-link fw-bold ${activeAsymmetricDeckTab === 'p2' ? 'active bg-info text-dark' : 'text-secondary'}`}
                                                            onClick={() => setActiveAsymmetricDeckTab('p2')}
                                                        >
                                                            <i className="bi bi-person-fill me-1"></i> Mazo Jugador 2 ({themeFormState.deckImagesP2?.length || 0})
                                                        </button>
                                                    </li>
                                                </ul>
                                                {activeAsymmetricDeckTab === 'p1'
                                                    ? renderDeckGrid(themeFormState.deckImagesP1 || [], 'deckImagesP1')
                                                    : renderDeckGrid(themeFormState.deckImagesP2 || [], 'deckImagesP2')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* PESTAÑA 4: JUGADORES & CELULAR                               */}
                            {/* ============================================================ */}
                            {activeTabKey === 'players' && (
                                <div className="row g-3">
                                    {/* JUGADOR 1 */}
                                    <div className="col-md-6 border-end pe-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-warning mb-3">
                                                <i className="bi bi-person-fill me-1"></i> Jugador 1 (Slot Izquierdo)
                                            </h6>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Nombre por Defecto J1</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={themeFormState.player1Name || ''}
                                                    onChange={(e) => handleFieldChange('player1Name', e.target.value)}
                                                    placeholder="Jugador 1"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Color Primario J1</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={themeFormState.player1Color || '#fbbf24'}
                                                        onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control text-uppercase"
                                                        value={themeFormState.player1Color || '#fbbf24'}
                                                        onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <DragAndDropField
                                                fieldLabel="Avatar Inicial J1"
                                                targetFieldName="player1Avatar"
                                                currentValue={themeFormState.player1Avatar}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player1Avatar'}
                                                helpText="Avatar o personaje (500x500 PNG)"
                                                onClear={() => handleFieldChange('player1Avatar', '')}
                                            />
                                            <DragAndDropField
                                                fieldLabel="Pose de Victoria J1 (Celebración)"
                                                targetFieldName="player1Winner"
                                                currentValue={themeFormState.player1Winner}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player1Winner'}
                                                helpText="Pose victoriosa (500x500 PNG)"
                                                onClear={() => handleFieldChange('player1Winner', '')}
                                            />
                                        </div>
                                    </div>

                                    {/* JUGADOR 2 */}
                                    <div className="col-md-6 ps-md-3">
                                        <div className="p-3 bg-light rounded-3 border">
                                            <h6 className="fw-bold text-info mb-3">
                                                <i className="bi bi-person-fill me-1"></i> Jugador 2 (Slot Derecho)
                                            </h6>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Nombre por Defecto J2</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={themeFormState.player2Name || ''}
                                                    onChange={(e) => handleFieldChange('player2Name', e.target.value)}
                                                    placeholder="Jugador 2"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label fw-bold">Color Primario J2</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={themeFormState.player2Color || '#60a5fa'}
                                                        onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control text-uppercase"
                                                        value={themeFormState.player2Color || '#60a5fa'}
                                                        onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <DragAndDropField
                                                fieldLabel="Avatar Inicial J2"
                                                targetFieldName="player2Avatar"
                                                currentValue={themeFormState.player2Avatar}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player2Avatar'}
                                                helpText="Avatar o personaje (500x500 PNG)"
                                                onClear={() => handleFieldChange('player2Avatar', '')}
                                            />
                                            <DragAndDropField
                                                fieldLabel="Pose de Victoria J2 (Celebración)"
                                                targetFieldName="player2Winner"
                                                currentValue={themeFormState.player2Winner}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player2Winner'}
                                                helpText="Pose victoriosa (500x500 PNG)"
                                                onClear={() => handleFieldChange('player2Winner', '')}
                                            />
                                        </div>
                                    </div>

                                    {/* Vista Móvil Celular Aficionado */}
                                    <div className="col-12">
                                        <hr className="my-2" />
                                        <h6 className="fw-bold text-primary mb-3">
                                            <i className="bi bi-phone me-1"></i> Pantalla de los Dispositivos Móviles (/memory)
                                        </h6>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo Móvil</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.fanBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('fanBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen Vertical / Patrón</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.fanBackgroundColor || '#0f172a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.fanBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo Móvil (1080x1920 Vertical)"
                                                targetFieldName="fanBackgroundUrl"
                                                currentValue={themeFormState.fanBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'fanBackgroundUrl'}
                                                onClear={() => handleFieldChange('fanBackgroundUrl', '')}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3 bg-light border-top d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={onCloseModal}
                                disabled={isSavingInProgress}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-success fw-bold px-4"
                                disabled={isSavingInProgress}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-1"></i> Guardar Tema
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

MemoryThemeModal.propTypes = {
    isOpen: PropTypes.bool,
    show: PropTypes.bool,
    onCloseModal: PropTypes.func,
    onClose: PropTypes.func,
    initialThemeData: PropTypes.object,
    themeToEdit: PropTypes.object,
    onThemeSavedSuccessfully: PropTypes.func,
    onSave: PropTypes.func,
    roomId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    operatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default MemoryThemeModal;
